/**
 * แอปและ client SSO — ลงทะเบียนแอป/role, สร้างและแก้ OIDC client, หมุน secret (D-175)
 *
 * `client_secret` เห็นได้ครั้งเดียวจากคำตอบของ create/rotate: เก็บใน state `issued`
 * ก้อนเดียวที่ถูกล้างเมื่อกดปิด ไม่ถูกส่งลง storage, URL, log หรือรายการ client
 * ที่โหลดใหม่ (คำตอบของ GET ไม่มี secret อยู่แล้ว)
 *
 * ปุ่มเขียนถูกกันที่นี่ด้วย `RBAC_ADMIN` CREATE/EDIT — เป็น UX เท่านั้น เซิร์ฟเวอร์ตัดสินเสมอ
 */

import { useEffect, useState } from 'react';

import { SsoAdminFailureNotice } from './SsoAdminFailureNotice';
import {
  diffEditForm,
  formatTimestamp,
  parseRoleLines,
  splitLines,
  toEditForm,
  type ClientEditForm,
} from './ssoAdminView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  addSsoAppRole,
  createSsoApp,
  createSsoClient,
  fetchSsoApps,
  fetchSsoClients,
  rotateSsoClientSecret,
  updateSsoClient,
  type SsoAdminFailure,
} from '../../lib/ssoAdmin/client';
import {
  SSO_ADMIN_MODULE,
  type SsoApp,
  type SsoIssuedClient,
  type SsoOauthClient,
} from '../../lib/ssoAdmin/types';

export interface SsoClientsScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type LoadState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | SsoAdminFailure;

interface IssuedSecret {
  readonly clientId: string;
  readonly secret: string;
  readonly kind: 'created' | 'rotated';
}

interface CreateForm {
  readonly client_id: string;
  readonly app_code: string;
  readonly client_name: string;
  readonly redirectText: string;
  readonly postLogoutText: string;
  readonly mfaRoles: readonly string[];
  readonly refresh_enabled: boolean;
  readonly allow_insecure_localhost: boolean;
}

const EMPTY_CREATE: CreateForm = {
  client_id: '',
  app_code: '',
  client_name: '',
  redirectText: '',
  postLogoutText: '',
  mfaRoles: [],
  refresh_enabled: false,
  allow_insecure_localhost: false,
};

function toggle(list: readonly string[], value: string, on: boolean): string[] {
  const without = list.filter((entry) => entry !== value);
  return on ? [...without, value] : without;
}

function activeRoles(apps: readonly SsoApp[], appCode: string): readonly string[] {
  const app = apps.find((candidate) => candidate.app_code === appCode);
  return app === undefined ? [] : app.roles.filter((r) => r.active).map((r) => r.role_code);
}

export function SsoClientsScreen({ permissions, apiBaseUrl, fetchImpl }: SsoClientsScreenProps) {
  const canCreate = can(permissions, SSO_ADMIN_MODULE, 'CREATE');
  const canEdit = can(permissions, SSO_ADMIN_MODULE, 'EDIT');

  const [apps, setApps] = useState<LoadState<SsoApp>>({ status: 'loading' });
  const [clients, setClients] = useState<LoadState<SsoOauthClient>>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<SsoAdminFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedSecret | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [editing, setEditing] = useState<{ client: SsoOauthClient; form: ClientEditForm } | null>(
    null,
  );
  const [rotateTarget, setRotateTarget] = useState<string | null>(null);

  const [appForm, setAppForm] = useState({ app_code: '', app_name: '', rolesText: '' });
  const [roleForm, setRoleForm] = useState({ app_code: '', role_code: '', description: '' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchSsoApps(options).then((result) => {
      if (active && result.status !== 'cancelled') {
        setApps(result);
      }
    });
    void fetchSsoClients(options).then((result) => {
      if (active && result.status !== 'cancelled') {
        setClients(result);
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const reload = () => {
    setReloadToken((token) => token + 1);
  };

  const appList = apps.status === 'ready' ? apps.items : [];
  const options = { apiBaseUrl, fetchImpl };

  /** รันคำขอเขียนหนึ่งครั้ง — ล้มแล้วไม่แตะตาราง สำเร็จแล้วโหลดจากเซิร์ฟเวอร์ใหม่ */
  const act = async <R extends { status: string }>(
    send: () => Promise<R>,
    onReady: (result: Extract<R, { status: 'ready' }>) => void,
  ) => {
    if (busy) {
      return;
    }
    setBusy(true);
    setFailure(null);
    setNotice(null);
    const result = await send();
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setFailure(result as unknown as SsoAdminFailure);
      return;
    }
    onReady(result as Extract<R, { status: 'ready' }>);
    reload();
  };

  const receiveSecret = (value: SsoIssuedClient, kind: IssuedSecret['kind']) => {
    setCopyState('idle');
    setIssued({ clientId: value.client.client_id, secret: value.clientSecret, kind });
  };

  const dismissSecret = () => {
    setIssued(null);
    setCopyState('idle');
  };

  const copySecret = async () => {
    if (issued === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const submitCreate = () => {
    const redirects = splitLines(createForm.redirectText);
    void act(
      () =>
        createSsoClient(
          {
            client_id: createForm.client_id.trim(),
            app_code: createForm.app_code,
            client_name: createForm.client_name.trim(),
            redirect_uris: redirects,
            post_logout_redirect_uris: splitLines(createForm.postLogoutText),
            mfa_roles: createForm.mfaRoles,
            refresh_enabled: createForm.refresh_enabled,
            allow_insecure_localhost: createForm.allow_insecure_localhost,
          },
          options,
        ),
      (result) => {
        receiveSecret(result, 'created');
        setCreateForm(EMPTY_CREATE);
      },
    );
  };

  const submitEdit = () => {
    if (editing === null) {
      return;
    }
    const body = diffEditForm(editing.client, editing.form);
    if (Object.keys(body).length === 0) {
      return;
    }
    void act(
      () => updateSsoClient(editing.client.client_id, body, options),
      (result) => {
        setNotice(`บันทึก client ${result.client.client_id} แล้ว`);
        setEditing(null);
      },
    );
  };

  const confirmRotate = () => {
    if (rotateTarget === null) {
      return;
    }
    const target = rotateTarget;
    void act(
      () => rotateSsoClientSecret(target, options),
      (result) => {
        receiveSecret(result, 'rotated');
        setRotateTarget(null);
      },
    );
  };

  const submitApp = () => {
    const roles = parseRoleLines(appForm.rolesText);
    if (roles === null) {
      return;
    }
    void act(
      () =>
        createSsoApp(
          { app_code: appForm.app_code.trim(), app_name: appForm.app_name.trim(), roles },
          options,
        ),
      (result) => {
        setNotice(`ลงทะเบียนแอป ${result.app.app_code} แล้ว`);
        setAppForm({ app_code: '', app_name: '', rolesText: '' });
      },
    );
  };

  const submitRole = () => {
    void act(
      () =>
        addSsoAppRole(
          roleForm.app_code,
          { role_code: roleForm.role_code.trim(), description: roleForm.description.trim() },
          options,
        ),
      (result) => {
        setNotice(`เพิ่ม role ให้แอป ${result.app.app_code} แล้ว`);
        setRoleForm({ app_code: roleForm.app_code, role_code: '', description: '' });
      },
    );
  };

  const createRoleOptions = activeRoles(appList, createForm.app_code);
  const editRoleOptions = editing === null ? [] : activeRoles(appList, editing.client.app_code);
  const editBody = editing === null ? {} : diffEditForm(editing.client, editing.form);
  const editRedirects = editing === null ? [] : splitLines(editing.form.redirectText);
  const appRolesParsed = parseRoleLines(appForm.rolesText);

  const setEdit = (patch: Partial<ClientEditForm>) => {
    setEditing((current) =>
      current === null ? null : { ...current, form: { ...current.form, ...patch } },
    );
  };

  return (
    <div className="cap-page" data-testid="sso-clients">
      <div className="cap-page__head">
        <h1 className="cap-page__title">
          แอปและ client SSO{' '}
          <InfoHint
            label="เกี่ยวกับหน้า SSO"
            text="ลงทะเบียนแอปอื่น (คลัง จัดซื้อ Tool & Die) ให้ล็อกอินผ่าน ERP นี้ด้วย OIDC: แต่ละ client มี secret ที่เห็นได้ครั้งเดียวตอนสร้างหรือหมุน"
          />
        </h1>
      </div>

      {!canCreate && !canEdit ? (
        <div className="cap-notice" data-testid="sso-clients-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <p className="cap-notice__text">
            สิทธิ์ชุดนี้ไม่มี CREATE/EDIT ของโมดูล {SSO_ADMIN_MODULE} ฟอร์มและปุ่มแก้ไขจึงไม่แสดง
            (เซิร์ฟเวอร์เป็นผู้ตัดสินจริงทุกคำขอ)
          </p>
        </div>
      ) : null}

      {issued === null ? null : (
        <section
          className="cap-notice"
          data-testid="sso-secret-panel"
          role="alert"
          aria-label="client secret"
        >
          <StatusBadge tone="danger">แสดงครั้งเดียว</StatusBadge>
          <h2 className="cap-notice__title">
            {issued.kind === 'created' ? 'สร้าง client แล้ว' : 'หมุน secret แล้ว'}:{' '}
            <code className="cap-table__code">{issued.clientId}</code>
          </h2>
          <p className="cap-notice__text">
            คัดลอก secret นี้เก็บไว้ในที่ปลอดภัยทันที{' '}
            <strong>จะไม่สามารถแสดงอีกได้</strong> — ระบบเก็บเพียงค่าแฮช ถ้าทำหายต้องหมุน secret ใหม่
            {issued.kind === 'rotated' ? ' (secret เดิมใช้ไม่ได้แล้วทันที)' : ''}
          </p>
          <p>
            <code className="cap-table__code" data-testid="sso-secret-value">
              {issued.secret}
            </code>
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="sso-secret-copy"
              onClick={() => {
                void copySecret();
              }}
            >
              คัดลอก
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="sso-secret-dismiss"
              onClick={dismissSecret}
            >
              ฉันเก็บไว้แล้ว ปิด
            </button>
            {copyState === 'copied' ? (
              <span className="cap-field__hint" data-testid="sso-secret-copied">
                คัดลอกแล้ว
              </span>
            ) : null}
            {copyState === 'failed' ? (
              <span className="cap-field__hint" data-testid="sso-secret-copy-failed">
                คัดลอกอัตโนมัติไม่ได้ — เลือกข้อความแล้วคัดลอกเอง
              </span>
            ) : null}
          </div>
        </section>
      )}

      {notice === null ? null : (
        <div className="cap-notice" data-testid="sso-notice" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <p className="cap-notice__text">{notice}</p>
        </div>
      )}

      {failure === null ? null : <SsoAdminFailureNotice failure={failure} onRetry={reload} />}

      {rotateTarget === null ? null : (
        <section className="cap-notice" data-testid="sso-rotate-confirm" role="alertdialog">
          <StatusBadge tone="warning">ยืนยันก่อนหมุน</StatusBadge>
          <h2 className="cap-notice__title">
            หมุน secret ของ <code className="cap-table__code">{rotateTarget}</code>?
          </h2>
          <p className="cap-notice__text">
            secret เดิมจะ<strong>ใช้ไม่ได้ทันที</strong> และ<strong>ทุกคนจะหลุดจากแอปนี้</strong>ต้องล็อกอินใหม่ แอปที่ใช้อยู่ต้องตั้งค่า secret ใหม่
            จึงจะล็อกอินได้ และ secret ใหม่จะแสดงให้เห็นครั้งเดียว
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="sso-rotate-confirm-yes"
              disabled={busy}
              onClick={confirmRotate}
            >
              {busy ? 'กำลังหมุน…' : 'ยืนยัน หมุน secret'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="sso-rotate-confirm-no"
              disabled={busy}
              onClick={() => {
                setRotateTarget(null);
              }}
            >
              ยกเลิก
            </button>
          </div>
        </section>
      )}

      {clients.status === 'loading' ? (
        <div className="cap-notice" data-testid="sso-clients-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการ client…</p>
        </div>
      ) : null}
      {clients.status !== 'loading' && clients.status !== 'ready' ? (
        <SsoAdminFailureNotice failure={clients} onRetry={reload} />
      ) : null}
      {apps.status !== 'loading' && apps.status !== 'ready' ? (
        <SsoAdminFailureNotice failure={apps} onRetry={reload} />
      ) : null}

      {clients.status === 'ready' && clients.items.length === 0 ? (
        <div className="cap-notice" data-testid="sso-clients-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มี client</StatusBadge>
          <p className="cap-notice__text">เซิร์ฟเวอร์ตอบสำเร็จแต่ยังไม่มี client ลงทะเบียนไว้</p>
        </div>
      ) : null}

      {clients.status === 'ready' && clients.items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="OIDC clients">
          <div className="cap-card__head">
            <h2 className="cap-card__title">OIDC clients</h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="sso-clients-table">
              <thead>
                <tr>
                  <th scope="col">client_id</th>
                  <th scope="col">แอป</th>
                  <th scope="col">ชื่อ</th>
                  <th scope="col">redirect URIs</th>
                  <th scope="col">MFA roles</th>
                  <th scope="col">refresh</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">หมุน secret ล่าสุด</th>
                  <th scope="col">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {clients.items.map((client) => (
                  <tr key={client.client_id} data-testid={`sso-client-row-${client.client_id}`}>
                    <th scope="row" className="cap-table__row-head">
                      <code className="cap-table__code">{client.client_id}</code>
                    </th>
                    <td>{client.app_code}</td>
                    <td>{client.client_name}</td>
                    <td>
                      {client.redirect_uris.map((uri) => (
                        <div key={uri}>
                          <code className="cap-table__code">{uri}</code>
                        </div>
                      ))}
                    </td>
                    <td>
                      {client.mfa_roles.length === 0
                        ? '—'
                        : client.mfa_roles.map((role) => (
                            <StatusBadge key={role} tone="info">
                              {role}
                            </StatusBadge>
                          ))}
                    </td>
                    <td>{client.refresh_enabled ? 'เปิด' : 'ปิด'}</td>
                    <td>
                      <StatusBadge tone={client.active ? 'success' : 'neutral'}>
                        {client.active ? 'ใช้งาน' : 'ปิดอยู่'}
                      </StatusBadge>
                    </td>
                    <td>{formatTimestamp(client.secret_rotated_at)}</td>
                    <td>
                      {canEdit ? (
                        <>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`sso-client-edit-${client.client_id}`}
                            disabled={busy}
                            onClick={() => {
                              setRotateTarget(null);
                              setEditing({ client, form: toEditForm(client) });
                            }}
                          >
                            แก้ไข
                          </button>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`sso-client-rotate-${client.client_id}`}
                            disabled={busy}
                            onClick={() => {
                              setEditing(null);
                              setRotateTarget(client.client_id);
                            }}
                          >
                            หมุน secret
                          </button>
                        </>
                      ) : (
                        <span className="cap-field__hint">ดูได้อย่างเดียว</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {canEdit && editing !== null ? (
        <form
          className="cap-card cap-card--form"
          data-testid="sso-client-edit-form"
          aria-label="แก้ไข client"
          onSubmit={(event) => {
            event.preventDefault();
            submitEdit();
          }}
        >
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              แก้ไข client <code className="cap-table__code">{editing.client.client_id}</code>
            </h2>
          </div>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">ชื่อ client</span>
              <input
                className="cap-input"
                data-testid="sso-edit-name"
                value={editing.form.client_name}
                onChange={(event) => {
                  setEdit({ client_name: event.target.value });
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">redirect URIs (หนึ่งบรรทัดต่อหนึ่ง URI)</span>
              <textarea
                className="cap-input"
                rows={3}
                data-testid="sso-edit-redirects"
                value={editing.form.redirectText}
                onChange={(event) => {
                  setEdit({ redirectText: event.target.value });
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">post-logout URIs (หนึ่งบรรทัดต่อหนึ่ง URI)</span>
              <textarea
                className="cap-input"
                rows={3}
                data-testid="sso-edit-post-logout"
                value={editing.form.postLogoutText}
                onChange={(event) => {
                  setEdit({ postLogoutText: event.target.value });
                }}
              />
            </label>
            <fieldset className="cap-field">
              <legend className="cap-field__label">role ที่ต้องยืนยัน MFA</legend>
              {editRoleOptions.length === 0 ? (
                <span className="cap-field__hint">แอปนี้ยังไม่มี role ที่ใช้งาน</span>
              ) : (
                editRoleOptions.map((role) => (
                  <label key={role}>
                    <input
                      type="checkbox"
                      data-testid={`sso-edit-mfa-${role}`}
                      checked={editing.form.mfaRoles.includes(role)}
                      onChange={(event) => {
                        setEdit({ mfaRoles: toggle(editing.form.mfaRoles, role, event.target.checked) });
                      }}
                    />{' '}
                    {role}
                  </label>
                ))
              )}
            </fieldset>
            <div className="cap-field">
              <label>
                <input
                  type="checkbox"
                  data-testid="sso-edit-refresh"
                  checked={editing.form.refresh_enabled}
                  onChange={(event) => {
                    setEdit({ refresh_enabled: event.target.checked });
                  }}
                />{' '}
                เปิด refresh token
              </label>
              <label>
                <input
                  type="checkbox"
                  data-testid="sso-edit-insecure"
                  checked={editing.form.allow_insecure_localhost}
                  onChange={(event) => {
                    setEdit({ allow_insecure_localhost: event.target.checked });
                  }}
                />{' '}
                อนุญาต localhost แบบไม่เข้ารหัส (พัฒนาเท่านั้น)
              </label>
              <label>
                <input
                  type="checkbox"
                  data-testid="sso-edit-require-app-role"
                  checked={editing.form.require_app_role}
                  onChange={(event) => {
                    setEdit({ require_app_role: event.target.checked });
                  }}
                />{' '}
                ต้องมี role ในแอปจึงล็อกอินได้
              </label>
              <label>
                <input
                  type="checkbox"
                  data-testid="sso-edit-active"
                  checked={editing.form.active}
                  onChange={(event) => {
                    setEdit({ active: event.target.checked });
                  }}
                />{' '}
                ใช้งาน (active)
              </label>
              {(editing.client.active && !editing.form.active) ||
              (editing.client.refresh_enabled && !editing.form.refresh_enabled) ? (
                <span className="cap-field__hint" data-testid="sso-edit-end-grants-warning">
                  การปิด active หรือ refresh จะยุติ grant/token ทั้งหมดของ client นี้ทันที
                </span>
              ) : null}
            </div>
          </div>
          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="sso-edit-save"
              disabled={
                busy ||
                Object.keys(editBody).length === 0 ||
                editing.form.client_name.trim() === '' ||
                editRedirects.length === 0
              }
            >
              {busy ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="sso-edit-cancel"
              onClick={() => {
                setEditing(null);
              }}
            >
              ยกเลิก
            </button>
          </div>
        </form>
      ) : null}

      {canCreate ? (
        <form
          className="cap-card cap-card--form"
          data-testid="sso-client-create-form"
          aria-label="สร้าง client"
          onSubmit={(event) => {
            event.preventDefault();
            submitCreate();
          }}
        >
          <div className="cap-card__head">
            <h2 className="cap-card__title">สร้าง client ใหม่</h2>
          </div>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">client_id</span>
              <input
                className="cap-input"
                data-testid="sso-create-client-id"
                autoComplete="off"
                value={createForm.client_id}
                onChange={(event) => {
                  setCreateForm({ ...createForm, client_id: event.target.value });
                }}
              />
              <span className="cap-field__hint">ตัวพิมพ์เล็ก ตัวเลข และ - เช่น store-web</span>
            </label>
            <label className="cap-field">
              <span className="cap-field__label">แอป</span>
              <select
                className="cap-input"
                data-testid="sso-create-app"
                value={createForm.app_code}
                onChange={(event) => {
                  setCreateForm({ ...createForm, app_code: event.target.value, mfaRoles: [] });
                }}
              >
                <option value="">— เลือกแอป —</option>
                {appList.map((app) => (
                  <option key={app.app_code} value={app.app_code}>
                    {app.app_code} — {app.app_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ชื่อ client</span>
              <input
                className="cap-input"
                data-testid="sso-create-name"
                value={createForm.client_name}
                onChange={(event) => {
                  setCreateForm({ ...createForm, client_name: event.target.value });
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">redirect URIs (หนึ่งบรรทัดต่อหนึ่ง URI)</span>
              <textarea
                className="cap-input"
                rows={3}
                data-testid="sso-create-redirects"
                value={createForm.redirectText}
                onChange={(event) => {
                  setCreateForm({ ...createForm, redirectText: event.target.value });
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">post-logout URIs (ไม่บังคับ)</span>
              <textarea
                className="cap-input"
                rows={3}
                data-testid="sso-create-post-logout"
                value={createForm.postLogoutText}
                onChange={(event) => {
                  setCreateForm({ ...createForm, postLogoutText: event.target.value });
                }}
              />
            </label>
            <fieldset className="cap-field">
              <legend className="cap-field__label">role ที่ต้องยืนยัน MFA</legend>
              {createRoleOptions.length === 0 ? (
                <span className="cap-field__hint">เลือกแอปที่มี role ก่อน</span>
              ) : (
                createRoleOptions.map((role) => (
                  <label key={role}>
                    <input
                      type="checkbox"
                      data-testid={`sso-create-mfa-${role}`}
                      checked={createForm.mfaRoles.includes(role)}
                      onChange={(event) => {
                        setCreateForm({
                          ...createForm,
                          mfaRoles: toggle(createForm.mfaRoles, role, event.target.checked),
                        });
                      }}
                    />{' '}
                    {role}
                  </label>
                ))
              )}
            </fieldset>
            <div className="cap-field">
              <label>
                <input
                  type="checkbox"
                  data-testid="sso-create-refresh"
                  checked={createForm.refresh_enabled}
                  onChange={(event) => {
                    setCreateForm({ ...createForm, refresh_enabled: event.target.checked });
                  }}
                />{' '}
                เปิด refresh token
              </label>
              <label>
                <input
                  type="checkbox"
                  data-testid="sso-create-insecure"
                  checked={createForm.allow_insecure_localhost}
                  onChange={(event) => {
                    setCreateForm({
                      ...createForm,
                      allow_insecure_localhost: event.target.checked,
                    });
                  }}
                />{' '}
                อนุญาต localhost แบบไม่เข้ารหัส (พัฒนาเท่านั้น)
              </label>
            </div>
          </div>
          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="sso-create-submit"
              disabled={
                busy ||
                createForm.client_id.trim().length < 3 ||
                createForm.app_code === '' ||
                createForm.client_name.trim() === '' ||
                splitLines(createForm.redirectText).length === 0
              }
            >
              {busy ? 'กำลังสร้าง…' : 'สร้าง client และออก secret'}
            </button>
          </div>
        </form>
      ) : null}

      {canCreate ? (
        <form
          className="cap-card cap-card--form"
          data-testid="sso-app-form"
          aria-label="ลงทะเบียนแอป"
          onSubmit={(event) => {
            event.preventDefault();
            submitApp();
          }}
        >
          <div className="cap-card__head">
            <h2 className="cap-card__title">ลงทะเบียนแอปพร้อม role</h2>
          </div>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">รหัสแอป</span>
              <input
                className="cap-input"
                data-testid="sso-app-code"
                autoComplete="off"
                value={appForm.app_code}
                onChange={(event) => {
                  setAppForm({ ...appForm, app_code: event.target.value });
                }}
              />
              <span className="cap-field__hint">ตัวพิมพ์ใหญ่ เช่น STORE</span>
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ชื่อแอป</span>
              <input
                className="cap-input"
                data-testid="sso-app-name"
                value={appForm.app_name}
                onChange={(event) => {
                  setAppForm({ ...appForm, app_name: event.target.value });
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">roles (บรรทัดละ: รหัส role เว้นวรรค คำอธิบาย)</span>
              <textarea
                className="cap-input"
                rows={3}
                data-testid="sso-app-roles"
                placeholder="KEEPER ผู้ดูแลคลัง"
                value={appForm.rolesText}
                onChange={(event) => {
                  setAppForm({ ...appForm, rolesText: event.target.value });
                }}
              />
            </label>
          </div>
          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="sso-app-submit"
              disabled={
                busy ||
                appForm.app_code.trim() === '' ||
                appForm.app_name.trim() === '' ||
                appRolesParsed === null
              }
            >
              ลงทะเบียนแอป
            </button>
          </div>
        </form>
      ) : null}

      {canEdit ? (
        <form
          className="cap-card cap-card--form"
          data-testid="sso-role-form"
          aria-label="เพิ่ม role ให้แอป"
          onSubmit={(event) => {
            event.preventDefault();
            submitRole();
          }}
        >
          <div className="cap-card__head">
            <h2 className="cap-card__title">เพิ่ม role ให้แอปที่มีอยู่</h2>
          </div>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">แอป</span>
              <select
                className="cap-input"
                data-testid="sso-role-app"
                value={roleForm.app_code}
                onChange={(event) => {
                  setRoleForm({ ...roleForm, app_code: event.target.value });
                }}
              >
                <option value="">— เลือกแอป —</option>
                {appList.map((app) => (
                  <option key={app.app_code} value={app.app_code}>
                    {app.app_code} — {app.app_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="cap-field">
              <span className="cap-field__label">รหัส role</span>
              <input
                className="cap-input"
                data-testid="sso-role-code"
                autoComplete="off"
                value={roleForm.role_code}
                onChange={(event) => {
                  setRoleForm({ ...roleForm, role_code: event.target.value });
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">คำอธิบาย</span>
              <input
                className="cap-input"
                data-testid="sso-role-description"
                value={roleForm.description}
                onChange={(event) => {
                  setRoleForm({ ...roleForm, description: event.target.value });
                }}
              />
            </label>
          </div>
          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="sso-role-submit"
              disabled={
                busy ||
                roleForm.app_code === '' ||
                roleForm.role_code.trim() === '' ||
                roleForm.description.trim() === ''
              }
            >
              เพิ่ม role
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
